import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, Calculator, TrendingUp, Shield, Users, CheckCircle, ArrowRight, Search, Heart, Key, Target, HandHeart, Award, Zap, Star, PiggyBank, DollarSign } from 'lucide-react';
import API_BASE_URL from '../config/api';
import SEO, { SEOPages } from '../components/SEO';

const Immobilier = () => {
  // États pour le calculateur d'hypothèque
  const [calculator, setCalculator] = useState({
    prixBien: 800000,
    apportPersonnel: 160000,
    duree: 15,
    tauxInteret: 2.5
  });

  const [calculatorResult, setCalculatorResult] = useState(null);
  const [swissfexRates, setSwissfexRates] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(true);
  const [ratesError, setRatesError] = useState(null);

  // États pour le calculateur de faisabilité d'achat
  const [faisabilite, setFaisabilite] = useState({
    prixBien: 750000,
    fondsPropresDispo: 150000,
    revenuAnnuelBrut: 150000,
    chargesMensuelles: 500,
    tauxInteret: 2.8
  });

  const [faisabiliteResult, setFaisabiliteResult] = useState(null);

  // Charger les taux SwissFex au chargement de la page
  useEffect(() => {
    fetchSwissfexRates();
  }, []);

  const fetchSwissfexRates = async () => {
    setRatesLoading(true);
    setRatesError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/swissfex-rates`);
      if (response.ok) {
        const data = await response.json();
        setSwissfexRates(data);
      } else {
        setRatesError('Impossible de charger les taux');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des taux SwissFex:', error);
      setRatesError('Erreur de connexion');
    } finally {
      setRatesLoading(false);
    }
  };

  // Helper function to get rate by duration key
  const getRateByDuration = (durationKey) => {
    if (!swissfexRates?.rates) return null;
    return swissfexRates.rates.find(r => r.duration === durationKey);
  };

  // Rate display configuration with colors
  const rateDisplayConfig = [
    { key: 'saron', label: 'SARON', sublabel: 'Variable', colorFrom: 'green-500', colorTo: 'emerald-500' },
    { key: '5_years', label: '5 ANS', sublabel: 'Fixe', colorFrom: 'blue-500', colorTo: 'cyan-500' },
    { key: '10_years', label: '10 ANS', sublabel: 'Fixe', colorFrom: 'purple-500', colorTo: 'violet-500' },
    { key: '15_years', label: '15 ANS', sublabel: 'Fixe', colorFrom: 'orange-500', colorTo: 'amber-500' },
    { key: '20_years', label: '20 ANS', sublabel: 'Fixe', colorFrom: 'red-500', colorTo: 'rose-500' },
    { key: '25_years', label: '25 ANS', sublabel: 'Fixe', colorFrom: 'pink-500', colorTo: 'fuchsia-500' },
  ];

  const courtageFeatures = [
    {
      icon: Heart,
      title: "Matching personnalisé",
      description: "Grâce à notre vaste réseau, nous connectons acheteurs et vendeurs avec une précision inégalée",
      highlight: "95% de satisfaction"
    },
    {
      icon: Target,
      title: "Estimation précise",
      description: "Évaluation professionnelle de votre bien basée sur l'analyse du marché local",
      highlight: "Expertise certifiée"
    },
    {
      icon: Key,
      title: "Négociation experte", 
      description: "Nos négociateurs expérimentés défendent vos intérêts pour obtenir le meilleur prix",
      highlight: "Économies moyennes 15k CHF"
    },
    {
      icon: HandHeart,
      title: "Accompagnement 360°",
      description: "De la recherche à la signature, nous gérons tous les aspects de votre projet",
      highlight: "Service complet"
    }
  ];

  // Calcul hypothèque standard
  const calculateMortgage = () => {
    const prix = parseFloat(calculator.prixBien);
    const apport = parseFloat(calculator.apportPersonnel);
    const duree = parseFloat(calculator.duree);
    const taux = parseFloat(calculator.tauxInteret) / 100;

    const montantFinance = prix - apport;
    const tauxMensuel = taux / 12;
    const nbMensualites = duree * 12;
    const mensualite = montantFinance * (tauxMensuel * Math.pow(1 + tauxMensuel, nbMensualites)) / (Math.pow(1 + tauxMensuel, nbMensualites) - 1);
    
    const coutTotal = mensualite * nbMensualites;
    const interetsTotal = coutTotal - montantFinance;
    const revenuMinimal = (mensualite * 12) / 0.33;

    setCalculatorResult({
      montantFinance: Math.round(montantFinance),
      mensualite: Math.round(mensualite),
      coutTotal: Math.round(coutTotal),
      interetsTotal: Math.round(interetsTotal),
      revenuMinimal: Math.round(revenuMinimal)
    });
  };

  // Calculer la faisabilité d'achat (règles suisses)
  const calculateFaisabilite = () => {
    const prix = parseFloat(faisabilite.prixBien);
    const fondsPropresDispo = parseFloat(faisabilite.fondsPropresDispo);
    const revenuAnnuel = parseFloat(faisabilite.revenuAnnuelBrut);
    const chargesMens = parseFloat(faisabilite.chargesMensuelles);
    const taux = parseFloat(faisabilite.tauxInteret) / 100;

    // Règle 1: Fonds propres minimum (20% du prix)
    const fondsPropresMini = prix * 0.20;
    const fondsPropresPct = (fondsPropresDispo / prix) * 100;
    const fondsPropresSuffisants = fondsPropresDispo >= fondsPropresMini;

    // Montant hypothécaire
    const montantHypotheque = prix - fondsPropresDispo;

    // Règle 2: Charges d'habitation max 33% du revenu brut
    // Charges = Intérêts théoriques (5%) + Amortissement (1%) + Entretien (1%)
    const tauxCalcul = 0.05; // Taux théorique FINMA pour calcul faisabilité
    const tauxAmortissement = 0.01;
    const tauxEntretien = 0.01;
    
    const chargesAnnuelles = montantHypotheque * (tauxCalcul + tauxAmortissement) + prix * tauxEntretien;
    const chargesMensuelles = chargesAnnuelles / 12;
    
    // Charges totales = charges habitation + charges existantes
    const chargesTotalesMens = chargesMensuelles + chargesMens;
    const revenuMensuel = revenuAnnuel / 12;
    const tauxEndettement = (chargesTotalesMens / revenuMensuel) * 100;
    const endettementOK = tauxEndettement <= 33;

    // Mensualité réelle avec taux actuel
    const tauxMensuel = taux / 12;
    const nbMois = 25 * 12; // 25 ans par défaut
    const mensualiteReelle = montantHypotheque * (tauxMensuel * Math.pow(1 + tauxMensuel, nbMois)) / (Math.pow(1 + tauxMensuel, nbMois) - 1);

    // Faisabilité globale
    const faisable = fondsPropresSuffisants && endettementOK;

    setFaisabiliteResult({
      // Fonds propres
      fondsPropresMini: Math.round(fondsPropresMini),
      fondsPropresPct: fondsPropresPct.toFixed(1),
      fondsPropresSuffisants,
      
      // Hypothèque
      montantHypotheque: Math.round(montantHypotheque),
      mensualiteReelle: Math.round(mensualiteReelle),
      
      // Taux d'endettement
      chargesHabitation: Math.round(chargesMensuelles),
      chargesExistantes: chargesMens,
      chargesTotales: Math.round(chargesTotalesMens),
      revenuMensuel: Math.round(revenuMensuel),
      tauxEndettement: tauxEndettement.toFixed(1),
      endettementOK,
      
      // Résultat global
      faisable
    });
  };

  const handleFaisabiliteChange = (field, value) => {
    setFaisabilite(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    calculateFaisabilite();
  }, [faisabilite]);

  const handleCalculatorChange = (field, value) => {
    setCalculator(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applySwissfexRate = (rateType, rate) => {
    setCalculator(prev => ({
      ...prev,
      tauxInteret: rate
    }));
    setFaisabilite(prev => ({
      ...prev,
      tauxInteret: rate
    }));
  };

  return (
    <div className="pt-20">
      <SEO {...SEOPages.immobilier} />
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          role="img"
          aria-label="Financement hypothécaire Suisse - maison moderne avec meilleurs taux hypothèque"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=1920&h=1080&fit=crop)',
            backgroundBlendMode: 'overlay'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40"></div>
        </div>
        
        <div className="container relative z-10">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold heading-display text-white mb-6">
              Solutions immobilières <span className="text-blue-400">complètes</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Financement, courtage et accompagnement : ATOM Group SA vous accompagne dans tous vos projets immobiliers 
              avec expertise et transparence.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center mt-12">
              <Link to="/contact" className="btn-glass btn-primary text-lg px-8 py-4 flex items-center justify-center">
                Consultation gratuite
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <button 
                onClick={() => document.getElementById('calculateur-faisabilite').scrollIntoView({behavior: 'smooth'})}
                className="btn-glass text-lg px-8 py-4 flex items-center justify-center"
              >
                <Calculator className="mr-2" size={20} />
                Calculateur de faisabilité
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Section Courtage Immobilier - HIGHLIGHT */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>
        <div className="container relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-6 py-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-full mb-6">
              <Award className="w-5 h-5 text-yellow-400 mr-2" />
              <span className="text-yellow-400 font-semibold">Service Exclusif</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold heading-display text-white mb-6">
              Courtage immobilier <span className="text-gradient">premium</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Grâce à notre <strong className="text-blue-400">vaste réseau exclusif</strong>, nous créons des connexions 
              parfaites entre acheteurs et vendeurs. Notre service de matching personnalisé vous fait gagner du temps, 
              de l'argent et vous garantit les meilleures opportunités du marché.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {courtageFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="glass p-8 rounded-3xl relative group hover:scale-102 transition-transform">
                  <div className="absolute top-4 right-4 md:top-4 md:right-4">
                    <div className="px-3 py-1 bg-gradient-to-r from-green-500/20 to-blue-500/20 rounded-full whitespace-nowrap">
                      <span className="text-green-400 text-xs font-semibold">{feature.highlight}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 mt-10 md:mt-0">
                    <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl p-3 flex-shrink-0 group-hover:scale-110 transition-transform">
                      <IconComponent className="w-full h-full text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold text-white mb-3">{feature.title}</h3>
                      <p className="text-gray-300 leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Call to Action Courtage */}
          <div className="text-center">
            <div className="glass p-8 rounded-3xl inline-block">
              <h3 className="text-2xl font-bold text-white mb-4">
                🎯 Prêt à trouver la perle rare ou l'acheteur idéal ?
              </h3>
              <p className="text-gray-300 mb-6 max-w-2xl">
                Notre réseau exclusif de plus de <strong className="text-blue-400">2'000 contacts</strong> actifs 
                nous permet de créer des matchs parfaits en un temps record.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact" className="btn-glass btn-primary px-8 py-4 flex items-center justify-center">
                  <Heart className="mr-2" size={20} />
                  Trouver mon match parfait
                </Link>
                <Link to="/contact" className="btn-glass px-8 py-4 flex items-center justify-center">
                  <Star className="mr-2" size={20} />
                  Faire estimer mon bien
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Financement Hypothécaire - CLASSIC */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold heading-display text-white mb-6">
              Financement <span className="text-gradient">hypothécaire</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Obtenez les meilleurs taux du marché grâce à notre expertise et nos partenariats privilégiés
            </p>
          </div>

          {/* Avantages du financement */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="glass p-8 rounded-2xl text-center group hover:scale-105 transition-transform">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl p-4 mx-auto mb-6 group-hover:scale-110 transition-transform">
                <TrendingUp className="w-full h-full text-white" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-4">Taux privilégiés</h3>
              <p className="text-gray-300 mb-4">
                Nos partenariats nous permettent de négocier des conditions exceptionnelles
              </p>
              <div className="text-green-400 font-bold text-lg">
                Jusqu'à -0.5% vs marché
              </div>
            </div>

            <div className="glass p-8 rounded-2xl text-center group hover:scale-105 transition-transform">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl p-4 mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Shield className="w-full h-full text-white" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-4">Accompagnement complet</h3>
              <p className="text-gray-300 mb-4">
                De l'analyse financière à la signature, nous gérons tout le processus
              </p>
              <div className="text-blue-400 font-bold text-lg">
                Service 360°
              </div>
            </div>

            <div className="glass p-8 rounded-2xl text-center group hover:scale-105 transition-transform">
              <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl p-4 mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Zap className="w-full h-full text-white" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-4">Rapidité</h3>
              <p className="text-gray-300 mb-4">
                Réponse de principe en 48h, dossier finalisé en 2 semaines
              </p>
              <div className="text-yellow-400 font-bold text-lg">
                Express & efficace
              </div>
            </div>
          </div>

          {/* Types d'hypothèques */}
          <div className="glass p-8 rounded-3xl mb-16">
            <h3 className="text-3xl font-bold text-white text-center mb-12">
              Nos solutions hypothécaires
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="p-6 bg-white/5 rounded-2xl">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl p-3 mr-4">
                    <Home className="w-full h-full text-white" />
                  </div>
                  <h4 className="text-xl font-semibold text-white">Hypothèque fixe</h4>
                </div>
                <p className="text-gray-300 mb-4">
                  Sécurité et prévisibilité avec un taux constant sur toute la durée
                </p>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li className="flex items-center"><CheckCircle size={16} className="text-green-400 mr-2" />Taux garanti</li>
                  <li className="flex items-center"><CheckCircle size={16} className="text-green-400 mr-2" />Budget prévisible</li>
                  <li className="flex items-center"><CheckCircle size={16} className="text-green-400 mr-2" />Protection hausse taux</li>
                </ul>
              </div>

              <div className="p-6 bg-white/5 rounded-2xl">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl p-3 mr-4">
                    <TrendingUp className="w-full h-full text-white" />
                  </div>
                  <h4 className="text-xl font-semibold text-white">Hypothèque variable</h4>
                </div>
                <p className="text-gray-300 mb-4">
                  Flexibilité et opportunité de profiter de la baisse des taux
                </p>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li className="flex items-center"><CheckCircle size={16} className="text-green-400 mr-2" />Taux avantageux</li>
                  <li className="flex items-center"><CheckCircle size={16} className="text-green-400 mr-2" />Flexibilité</li>
                  <li className="flex items-center"><CheckCircle size={16} className="text-green-400 mr-2" />Résiliation possible</li>
                </ul>
              </div>

              <div className="p-6 bg-white/5 rounded-2xl">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-3 mr-4">
                    <Calculator className="w-full h-full text-white" />
                  </div>
                  <h4 className="text-xl font-semibold text-white">SARON</h4>
                </div>
                <p className="text-gray-300 mb-4">
                  Nouveau référentiel suisse, souvent plus avantageux que le Libor
                </p>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li className="flex items-center"><CheckCircle size={16} className="text-green-400 mr-2" />Taux attractif</li>
                  <li className="flex items-center"><CheckCircle size={16} className="text-green-400 mr-2" />Référence CH</li>
                  <li className="flex items-center"><CheckCircle size={16} className="text-green-400 mr-2" />Innovation</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Taux SwissFex en temps réel - DYNAMIQUE */}
          <div className="glass p-8 rounded-3xl mb-16">
            <div className="text-center mb-8">
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500/20 to-blue-500/20 rounded-full mb-4">
                <TrendingUp className="w-4 h-4 text-green-400 mr-2" />
                <span className="text-green-400 font-semibold text-sm">
                  {ratesLoading ? 'Chargement...' : 'Taux en temps réel'}
                </span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Taux hypothécaires suisses</h3>
              
              {/* Source et date de mise à jour */}
              {swissfexRates && (
                <div className="flex flex-col items-center gap-2">
                  <p className="text-gray-300 text-sm">
                    Mise à jour: {new Date(swissfexRates.last_updated).toLocaleDateString('fr-CH', {
                      day: '2-digit',
                      month: '2-digit', 
                      year: 'numeric'
                    })}
                  </p>
                </div>
              )}
            </div>
            
            {/* État de chargement */}
            {ratesLoading && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="p-4 bg-white/5 rounded-xl animate-pulse">
                    <div className="h-4 bg-white/10 rounded mb-2"></div>
                    <div className="h-8 bg-white/10 rounded mb-2"></div>
                    <div className="h-3 bg-white/10 rounded w-1/2 mx-auto"></div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Erreur */}
            {ratesError && !ratesLoading && (
              <div className="text-center py-8">
                <p className="text-red-400 mb-4">{ratesError}</p>
                <button
                  onClick={fetchSwissfexRates}
                  className="btn-glass px-6 py-2 text-sm"
                >
                  Réessayer
                </button>
              </div>
            )}
            
            {/* Affichage des taux dynamiques */}
            {!ratesLoading && !ratesError && swissfexRates?.rates && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {/* SARON */}
                  {getRateByDuration('saron') && (
                    <button
                      onClick={() => applySwissfexRate('saron', getRateByDuration('saron').rate)}
                      className="group p-4 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl hover:from-green-500/20 hover:to-emerald-500/20 transition-all hover:scale-105 border border-green-500/20 hover:border-green-400/50"
                    >
                      <div className="text-center">
                        <div className="text-xs text-green-400 mb-1 uppercase tracking-wide font-semibold">SARON</div>
                        <div className="text-2xl font-bold text-white group-hover:text-green-400 transition-colors">
                          {getRateByDuration('saron').rate.toFixed(2)}%
                        </div>
                        <div className="text-xs text-green-400 mt-1 opacity-60">Variable</div>
                      </div>
                    </button>
                  )}
                  
                  {/* 5 ANS */}
                  {getRateByDuration('5_years') && (
                    <button
                      onClick={() => applySwissfexRate('5_years', getRateByDuration('5_years').rate)}
                      className="group p-4 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl hover:from-blue-500/20 hover:to-cyan-500/20 transition-all hover:scale-105 border border-blue-500/20 hover:border-blue-400/50"
                    >
                      <div className="text-center">
                        <div className="text-xs text-blue-400 mb-1 uppercase tracking-wide font-semibold">5 ANS</div>
                        <div className="text-2xl font-bold text-white group-hover:text-blue-400 transition-colors">
                          {getRateByDuration('5_years').rate.toFixed(2)}%
                        </div>
                        <div className="text-xs text-blue-400 mt-1 opacity-60">Fixe</div>
                      </div>
                    </button>
                  )}
                  
                  {/* 10 ANS */}
                  {getRateByDuration('10_years') && (
                    <button
                      onClick={() => applySwissfexRate('10_years', getRateByDuration('10_years').rate)}
                      className="group p-4 bg-gradient-to-br from-purple-500/10 to-violet-500/10 rounded-xl hover:from-purple-500/20 hover:to-violet-500/20 transition-all hover:scale-105 border border-purple-500/20 hover:border-purple-400/50"
                    >
                      <div className="text-center">
                        <div className="text-xs text-purple-400 mb-1 uppercase tracking-wide font-semibold">10 ANS</div>
                        <div className="text-2xl font-bold text-white group-hover:text-purple-400 transition-colors">
                          {getRateByDuration('10_years').rate.toFixed(2)}%
                        </div>
                        <div className="text-xs text-purple-400 mt-1 opacity-60">Fixe</div>
                      </div>
                    </button>
                  )}
                  
                  {/* 15 ANS */}
                  {getRateByDuration('15_years') && (
                    <button
                      onClick={() => applySwissfexRate('15_years', getRateByDuration('15_years').rate)}
                      className="group p-4 bg-gradient-to-br from-orange-500/10 to-amber-500/10 rounded-xl hover:from-orange-500/20 hover:to-amber-500/20 transition-all hover:scale-105 border border-orange-500/20 hover:border-orange-400/50"
                    >
                      <div className="text-center">
                        <div className="text-xs text-orange-400 mb-1 uppercase tracking-wide font-semibold">15 ANS</div>
                        <div className="text-2xl font-bold text-white group-hover:text-orange-400 transition-colors">
                          {getRateByDuration('15_years').rate.toFixed(2)}%
                        </div>
                        <div className="text-xs text-orange-400 mt-1 opacity-60">Fixe</div>
                      </div>
                    </button>
                  )}
                  
                  {/* 20 ANS */}
                  {getRateByDuration('20_years') && (
                    <button
                      onClick={() => applySwissfexRate('20_years', getRateByDuration('20_years').rate)}
                      className="group p-4 bg-gradient-to-br from-red-500/10 to-rose-500/10 rounded-xl hover:from-red-500/20 hover:to-rose-500/20 transition-all hover:scale-105 border border-red-500/20 hover:border-red-400/50"
                    >
                      <div className="text-center">
                        <div className="text-xs text-red-400 mb-1 uppercase tracking-wide font-semibold">20 ANS</div>
                        <div className="text-2xl font-bold text-white group-hover:text-red-400 transition-colors">
                          {getRateByDuration('20_years').rate.toFixed(2)}%
                        </div>
                        <div className="text-xs text-red-400 mt-1 opacity-60">Fixe</div>
                      </div>
                    </button>
                  )}
                  
                  {/* 25 ANS */}
                  {getRateByDuration('25_years') && (
                    <button
                      onClick={() => applySwissfexRate('25_years', getRateByDuration('25_years').rate)}
                      className="group p-4 bg-gradient-to-br from-pink-500/10 to-fuchsia-500/10 rounded-xl hover:from-pink-500/20 hover:to-fuchsia-500/20 transition-all hover:scale-105 border border-pink-500/20 hover:border-pink-400/50"
                    >
                      <div className="text-center">
                        <div className="text-xs text-pink-400 mb-1 uppercase tracking-wide font-semibold">25 ANS</div>
                        <div className="text-2xl font-bold text-white group-hover:text-pink-400 transition-colors">
                          {getRateByDuration('25_years').rate.toFixed(2)}%
                        </div>
                        <div className="text-xs text-pink-400 mt-1 opacity-60">Fixe</div>
                      </div>
                    </button>
                  )}
                </div>
                
                <div className="text-center mt-6">
                  <p className="text-xs text-gray-400">
                    Cliquez sur un taux pour l'appliquer automatiquement au calculateur • Taux indicatifs "à partir de"
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Calculateur de Faisabilité d'Achat Immobilier */}
      <section id="calculateur-faisabilite" className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Calculateur de <span className="text-blue-400">faisabilité d'achat</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Découvrez instantanément si votre projet d'achat immobilier est réalisable selon les critères bancaires suisses
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="glass p-8 rounded-3xl">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                
                {/* Paramètres */}
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold text-white mb-6">Vos paramètres</h3>
                  
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Prix du bien (CHF)</label>
                    <input
                      type="number"
                      value={faisabilite.prixBien}
                      onChange={(e) => handleFaisabiliteChange('prixBien', e.target.value)}
                      className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all duration-300"
                      min="100000"
                      step="10000"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Fonds propres disponibles (CHF)</label>
                    <input
                      type="number"
                      value={faisabilite.fondsPropresDispo}
                      onChange={(e) => handleFaisabiliteChange('fondsPropresDispo', e.target.value)}
                      className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all duration-300"
                      min="0"
                      step="5000"
                    />
                    <p className="text-xs text-gray-400 mt-1">Minimum 20% du prix (économies, 2e/3e pilier, héritage, donation)</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Revenu annuel brut (CHF)</label>
                    <input
                      type="number"
                      value={faisabilite.revenuAnnuelBrut}
                      onChange={(e) => handleFaisabiliteChange('revenuAnnuelBrut', e.target.value)}
                      className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all duration-300"
                      min="30000"
                      step="5000"
                    />
                    <p className="text-xs text-gray-400 mt-1">Revenu total du ménage avant déductions</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Charges mensuelles existantes (CHF)</label>
                    <input
                      type="number"
                      value={faisabilite.chargesMensuelles}
                      onChange={(e) => handleFaisabiliteChange('chargesMensuelles', e.target.value)}
                      className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all duration-300"
                      min="0"
                      step="100"
                    />
                    <p className="text-xs text-gray-400 mt-1">Crédits, prêts, pensions alimentaires, etc.</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Taux d'intérêt actuel (%)</label>
                    <input
                      type="number"
                      value={faisabilite.tauxInteret}
                      onChange={(e) => handleFaisabiliteChange('tauxInteret', e.target.value)}
                      className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all duration-300"
                      min="0.1"
                      max="10"
                      step="0.1"
                    />
                    <p className="text-xs text-gray-400 mt-1">Pour le calcul de la mensualité réelle</p>
                  </div>
                </div>
                
                {/* Résultats */}
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold text-white mb-6">Analyse de faisabilité</h3>
                  
                  {faisabiliteResult && (
                    <div className="space-y-6">
                      
                      {/* Verdict principal */}
                      <div className={`p-6 rounded-2xl border-2 text-center ${
                        faisabiliteResult.faisable 
                          ? 'border-green-500/50 bg-green-500/10' 
                          : 'border-red-500/50 bg-red-500/10'
                      }`}>
                        <div className={`text-3xl font-bold mb-2 ${
                          faisabiliteResult.faisable ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {faisabiliteResult.faisable 
                            ? '✅ PROJET RÉALISABLE' 
                            : '⚠️ CONDITIONS NON REMPLIES'
                          }
                        </div>
                        <div className="text-gray-300 text-sm">
                          Selon les critères bancaires suisses
                        </div>
                      </div>
                      
                      {/* Critère 1: Fonds propres */}
                      <div className="glass p-6 rounded-2xl">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center">
                            <PiggyBank className="w-6 h-6 text-blue-400 mr-3" />
                            <h4 className="text-lg font-semibold text-white">Fonds propres</h4>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                            faisabiliteResult.fondsPropresSuffisants 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {faisabiliteResult.fondsPropresSuffisants ? '✓ OK' : '✗ INSUFFISANT'}
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-300">Fonds propres disponibles:</span>
                            <span className="text-white font-semibold">CHF {faisabilite.fondsPropresDispo.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300">Minimum requis (20%):</span>
                            <span className="text-white">CHF {faisabiliteResult.fondsPropresMini.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between font-bold border-t border-gray-600 pt-2">
                            <span className="text-white">Votre apport:</span>
                            <span className={faisabiliteResult.fondsPropresSuffisants ? 'text-green-400' : 'text-red-400'}>
                              {faisabiliteResult.fondsPropresPct}%
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Critère 2: Taux d'endettement */}
                      <div className="glass p-6 rounded-2xl">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center">
                            <TrendingUp className="w-6 h-6 text-purple-400 mr-3" />
                            <h4 className="text-lg font-semibold text-white">Taux d'endettement</h4>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                            faisabiliteResult.endettementOK 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {faisabiliteResult.endettementOK ? '✓ OK' : '✗ TROP ÉLEVÉ'}
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-300">Charges habitation (théorique 5%):</span>
                            <span className="text-white">CHF {faisabiliteResult.chargesHabitation.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300">Charges existantes:</span>
                            <span className="text-white">CHF {faisabiliteResult.chargesExistantes.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300">Charges totales:</span>
                            <span className="text-white font-semibold">CHF {faisabiliteResult.chargesTotales.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300">Revenu mensuel:</span>
                            <span className="text-white">CHF {faisabiliteResult.revenuMensuel.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between font-bold border-t border-gray-600 pt-2">
                            <span className="text-white">Taux d'endettement:</span>
                            <span className={faisabiliteResult.endettementOK ? 'text-green-400' : 'text-red-400'}>
                              {faisabiliteResult.tauxEndettement}% / 33% max
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Mensualité réelle */}
                      <div className="glass p-6 rounded-2xl bg-gradient-to-r from-blue-500/10 to-purple-500/10">
                        <h4 className="text-lg font-semibold text-white mb-4 text-center">💰 Mensualité estimée</h4>
                        <div className="text-center">
                          <div className="text-4xl font-bold text-blue-400 mb-2">
                            CHF {faisabiliteResult.mensualiteReelle.toLocaleString()}
                          </div>
                          <div className="text-sm text-gray-300">
                            Hypothèque de CHF {faisabiliteResult.montantHypotheque.toLocaleString()} à {faisabilite.tauxInteret}% sur 25 ans
                          </div>
                          <div className="text-xs text-gray-400 mt-2">
                            + charges d'entretien (env. 1% du prix/an)
                          </div>
                        </div>
                      </div>
                      
                      {/* Conseils */}
                      {!faisabiliteResult.faisable && (
                        <div className="glass p-6 rounded-2xl bg-yellow-500/10 border border-yellow-500/30">
                          <h4 className="text-lg font-semibold text-yellow-400 mb-3">💡 Recommandations</h4>
                          <ul className="space-y-2 text-sm text-gray-300">
                            {!faisabiliteResult.fondsPropresSuffisants && (
                              <li className="flex items-start">
                                <span className="mr-2">•</span>
                                <span>Augmentez vos fonds propres à minimum CHF {faisabiliteResult.fondsPropresMini.toLocaleString()}</span>
                              </li>
                            )}
                            {!faisabiliteResult.endettementOK && (
                              <li className="flex items-start">
                                <span className="mr-2">•</span>
                                <span>Réduisez vos charges existantes ou augmentez vos revenus pour respecter le ratio 33%</span>
                              </li>
                            )}
                            <li className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>Consultez nos experts pour une analyse personnalisée de votre situation</span>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="mt-12 text-center">
                <div className="glass p-8 rounded-3xl">
                  <h4 className="text-2xl font-bold text-white mb-4">
                    Besoin d'un conseil personnalisé ?
                  </h4>
                  <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                    Nos experts analysent votre dossier complet et vous accompagnent dans toutes les démarches
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/contact" className="btn-glass btn-primary px-8 py-3">
                      Consultation gratuite
                      <ArrowRight className="ml-2" size={20} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20">
        <div className="container">
          <div className="glass p-12 rounded-3xl text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Votre projet immobilier commence ici
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Que vous souhaitiez acheter, vendre ou financer, notre équipe d'experts vous accompagne 
              avec passion et expertise pour concrétiser vos rêves immobiliers.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact" className="btn-glass btn-primary text-lg px-12 py-4 flex items-center justify-center">
                <Heart className="mr-2" size={20} />
                Démarrer mon projet
              </Link>
              <Link to="/contact" className="btn-glass text-lg px-12 py-4 flex items-center justify-center">
                Consultation gratuite
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Immobilier;